import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../../../contexts/NotyfContext";
import {
  deleteReportFromHistory,
  getUserReports,
} from "../../../../redux/slices/reports";
import {
  useTable,
  useRowSelect,
  usePagination,
  useSortBy,
  useFilters,
  useGlobalFilter,
} from "react-table";
import { useTranslation } from "react-i18next";
import getReportsHistoryData from "./reportsHistoryData";
import {
  ButtonGroup,
  Card,
  Dropdown,
  DropdownButton,
  Table,
} from "react-bootstrap";
import { FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import DefaultColumnFilter from "../../../../utils/DefaultColumnFilter";
import Loader from "../../../../components/spinners/Loader";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import { Helmet } from "react-helmet-async";

const dummyReportHistory = [
  {
    id: 1,
    name: "Monthly Usage Report",
    createdAt: "2025-07-01 08:00:00",
    finishedAt: "2025-07-01 08:05:00",
    url: "/reports/monthly_usage_july.pdf",
  },
  {
    id: 2,
    name: "Weekly Invoice Summary",
    createdAt: "2025-07-15 09:00:00",
    finishedAt: "2025-07-15 09:01:30",
    url: "/reports/invoice_summary_week29.csv",
  },
  {
    id: 3,
    name: "SIM Data Session Logs",
    createdAt: "2025-07-17 12:00:00",
    finishedAt: null, // still processing
    url: "",
  },
  {
    id: 4,
    name: "Alerts Overview",
    createdAt: "2025-07-10 06:30:00",
    finishedAt: "2025-07-10 06:32:10",
    url: "/reports/alerts_overview_july.pdf",
  },
];

const ReportsHistory = ({ demoMode = false }) => {
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const { faviconUrl, backgroundColor, fontColor } = useWhitelabel();
  const reportHistory = useSelector((state) => state.reports.reportHistory);
  const [activeUser, setActiveUser] = useState({});
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const isDemoOrLead = demoMode || activeUser.role === "lead";
  const [isLoading, setIsLoading] = useState(!isDemoOrLead ? true : false);
  const { t } = useTranslation();
  const { reportsHistoryColumns } = useMemo(
    () => getReportsHistoryData(t, isDemoOrLead),
    [t, isDemoOrLead]
  );

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchReportsHistory = async () => {
      await dispatch(getUserReports())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "right",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        });
    };

    if (activeUser.id && !isDemoOrLead) {
      fetchReportsHistory();
    }
  }, [activeUser, isDemoOrLead, dispatch, notyf]);

  const columns = useMemo(() => {
    if (!isDemoOrLead) {
      return [
        ...reportsHistoryColumns,
        {
          id: "actions",
          Header: t("reports.history.tableHeaders.actions"),
          Cell: ({ row }) => {
            return (
              <DropdownButton
                data-boundary="viewport"
                as={ButtonGroup}
                className="actions-dropdown"
                title={t("reports.history.tableHeaders.actions")}
                style={{
                  "--dynamic-bg-color": backgroundColor,
                  "--dynamic-font-color": fontColor,
                  border: "none",
                }}
              >
                <Dropdown.Item
                  onClick={async () => {
                    await dispatch(
                      deleteReportFromHistory({ id: row.original.id })
                    )
                      .then(() => {
                        notyf.open({
                          type: "success",
                          message: "Report deleted successfully",
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: { x: "center", y: "top" },
                        });
                      })
                      .catch((error) => {
                        notyf.open({
                          type: "danger",
                          message: error,
                          duration: 5000,
                          ripple: true,
                          dismissible: false,
                          position: { x: "center", y: "top" },
                        });
                      });
                  }}
                >
                  <FaTrash size={18} />
                  {t("reports.history.delete")}
                </Dropdown.Item>
              </DropdownButton>
            );
          },
        },
      ];
    } else {
      return [...reportsHistoryColumns];
    }
  }, [
    t,
    isDemoOrLead,
    reportsHistoryColumns,
    backgroundColor,
    fontColor,
    dispatch,
    notyf,
  ]);

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const data = useMemo(
    () => (isDemoOrLead === false ? reportHistory : dummyReportHistory),
    [reportHistory, isDemoOrLead]
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns: columns,
        data,
        initialState: { pageIndex: 0 },
        defaultColumn,
        filterTypes,
      },
      useFilters,
      useGlobalFilter,
      useSortBy,
      usePagination,
      useRowSelect
    );

  if (isLoading) {
    return (
      <React.Fragment>
        <Helmet title="Reports History">
          <link rel="icon" href={faviconUrl} />
        </Helmet>
        <Card>
          <Card.Body>
            <Loader />
          </Card.Body>
        </Card>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <Helmet title="Reports History">
        <link rel="icon" href={faviconUrl} />
      </Helmet>
      <Card>
        <Card.Header>
          <Card.Title>{t("reports.history.title")}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Table id="clientsTable" hover responsive {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      <span {...column.getSortByToggleProps()}>
                        {column.render("Header")}
                        <span>
                          {column.sortable ? (
                            column.isSorted ? (
                              column.isSortedDesc ? (
                                <FaSortUp className="ms-2 " />
                              ) : (
                                <FaSortDown className="ms-2 " />
                              )
                            ) : (
                              <FaSort className="ms-2" />
                            )
                          ) : null}
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default ReportsHistory;
